import React, { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import { useDebouncedValue } from '@mantine/hooks';
import { Period } from 'components/form-fields/range-pickers/DateRangePicker';
import dayjs from 'dayjs';
import { EventType } from 'types';

// Define the filters state type (UI state)
export interface UncreditedReservationsFilters {
  athletes: string[];
  dateRange: Period;
  eventTypes: EventType[];
  search: string;
}

// Define the computed filters type (API parameters)
export interface ComputedFilters {
  startDate: string;
  endDate: string;
  search: string;
  eventTypeIds?: string[];
  athleteIds: string[];
}

interface UncreditedReservationsContextValue {
  filters: UncreditedReservationsFilters;
  computedFilters: ComputedFilters;
  setAthletes: (athletes: string[]) => void;
  setDateRange: (dateRange: Period) => void;
  setEventTypes: (eventTypes: EventType[]) => void;
  setSearch: (search: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

const UncreditedReservationsContext = createContext<UncreditedReservationsContextValue | undefined>(
  undefined,
);

const getDefaultFilters = (): UncreditedReservationsFilters => ({
  athletes: [],
  dateRange: {
    period: 'next_7_days',
    startDate: dayjs().toDate(),
    endDate: dayjs().add(6, 'day').toDate(),
  },
  eventTypes: [],
  search: '',
});

interface UncreditedReservationsProviderProps {
  children: ReactNode;
}

export const UncreditedReservationsProvider = ({
  children,
}: UncreditedReservationsProviderProps): JSX.Element => {
  const [filters, setFilters] = React.useState<UncreditedReservationsFilters>(getDefaultFilters());
  const [debouncedSearch] = useDebouncedValue(filters.search, 350);

  // Computed filters for API - memoized to prevent unnecessary recalculations
  const computedFilters = useMemo<ComputedFilters>(() => {
    const computed: ComputedFilters = {
      startDate: dayjs(filters.dateRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(filters.dateRange.endDate).format('YYYY-MM-DD'),
      search: debouncedSearch,
      athleteIds: filters.athletes,
    };

    if (filters.eventTypes.length > 0) {
      computed.eventTypeIds = filters.eventTypes.map(et => et.eventTypeId);
    }

    return computed;
  }, [
    filters.dateRange.startDate,
    filters.dateRange.endDate,
    debouncedSearch,
    filters.eventTypes,
    filters.athletes,
  ]);

  const setAthletes = useCallback((athletes: string[]) => {
    setFilters(prev => ({ ...prev, athletes }));
  }, []);

  const setDateRange = useCallback((dateRange: Period) => {
    setFilters(prev => ({ ...prev, dateRange }));
  }, []);

  const setEventTypes = useCallback((eventTypes: EventType[]) => {
    setFilters(prev => ({ ...prev, eventTypes }));
  }, []);

  const setSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(getDefaultFilters());
  }, []);

  const hasActiveFilters = useMemo(() => {
    return filters.athletes.length > 0 || filters.eventTypes.length > 0 || filters.search.trim() !== '';
  }, [filters.athletes, filters.eventTypes, filters.search]);

  const value = useMemo(
    () => ({
      filters,
      computedFilters,
      setAthletes,
      setDateRange,
      setEventTypes,
      setSearch,
      clearFilters,
      hasActiveFilters,
    }),
    [
      filters,
      computedFilters,
      setAthletes,
      setDateRange,
      setEventTypes,
      setSearch,
      clearFilters,
      hasActiveFilters,
    ],
  );

  return (
    <UncreditedReservationsContext.Provider value={value}>{children}</UncreditedReservationsContext.Provider>
  );
};

// Custom hook to use the context
export const useUncreditedReservationsContext = (): UncreditedReservationsContextValue => {
  const context = useContext(UncreditedReservationsContext);

  if (context === undefined) {
    throw new Error('useUncreditedReservationsContext must be used within UncreditedReservationsProvider');
  }

  return context;
};
